import { useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { ChatBubbleOutline } from '@mui/icons-material';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Typography from '@/components/ui/Typography/Typography';
import { useChat } from '@/providers/ChatProvider/ChatProvider';
import { useJobs } from '@/providers/JobsProvider/JobsProvider';
import type { ChatChannel, Job } from '@/types/types';
import type { ChannelSummary, ChannelsFilterId } from './IChannelsPopover';
import { styles } from './styles';

const CHANNEL_LIMIT = 10;

export interface ChannelsPopoverProps {
    channels?: ChannelSummary[];
    onChannelClick?: (channel: ChannelSummary) => void;
}

const tabs: { id: ChannelsFilterId; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
];

const buildChannelInitials = (name: string): string => {
    const words = name.split(/[\s-]+/).filter(Boolean);
    if (!words.length) return name.slice(0, 2).toUpperCase();
    return words
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join('');
};

const collectChannels = (jobs: Job[], activeJobId: string | null): ChannelSummary[] => {
    const sourceJobs = activeJobId
        ? jobs.filter((j) => j.id === activeJobId)
        : jobs;

    const map = new Map<string, ChannelSummary>();
    sourceJobs.forEach((job) => {
        const raw = job.chat?.channels;
        if (!raw) return;
        const list: ChatChannel[] = Array.isArray(raw) ? raw : Object.values(raw);
        const jobSuffix = job.vehicle?.vin ? `- ${job.vehicle.vin.slice(-6)}` : '';
        list.forEach((ch) => {
            if (!ch.channel_url || map.has(ch.channel_url)) return;
            const repairerName =
                job.services.find((s) => s.repair_company?.id === ch.company_id)
                    ?.repair_company?.name ??
                ch.company_name ??
                'Channel';
            map.set(ch.channel_url, {
                id: ch.channel_url,
                name: `${repairerName} ${jobSuffix}`.trim(),
                unreadCount: 0,
                lastMessage: 'No messages yet',
                initials: buildChannelInitials(repairerName),
            });
        });
    });
    return Array.from(map.values()).slice(0, CHANNEL_LIMIT);
};

const ChannelsPopover = ({
    channels,
    onChannelClick,
}: ChannelsPopoverProps) => {
    const theme = useTheme();
    const jobsCtx = useJobs();
    const chat = useChat();
    const [filter, setFilter] = useState<ChannelsFilterId>('all');

    const items: ChannelSummary[] = useMemo(
        () => channels ?? collectChannels(jobsCtx.state.jobs, jobsCtx.state.activeJob),
        [channels, jobsCtx.state.jobs, jobsCtx.state.activeJob],
    );

    const filtered = useMemo(() => {
        if (filter === 'unread') return items.filter((c) => c.unreadCount > 0);
        return items;
    }, [items, filter]);

    const handleClick = (channel: ChannelSummary) => {
        onChannelClick?.(channel);
        chat.openWithChannel({ url: channel.id, name: channel.name });
    };

    return (
        <Box sx={styles.root}>
            <Box sx={styles.header}>
                <Typography sx={styles.title}>Channels</Typography>
                <Box sx={styles.tabsRow}>
                    {tabs.map((t) => (
                        <Button
                            key={t.id}
                            sx={styles.tabBtn(filter === t.id, theme)}
                            onClick={() => setFilter(t.id)}
                        >
                            {t.label}
                        </Button>
                    ))}
                </Box>
            </Box>
            <Box sx={styles.list}>
                {filtered.length === 0 ? (
                    <Box sx={styles.emptyState}>
                        <ChatBubbleOutline sx={{ fontSize: 80, color: 'grey.300' }} />
                        <Typography>No channels</Typography>
                    </Box>
                ) : (
                    filtered.map((c) => (
                        <Box
                            key={c.id}
                            sx={styles.channelRow(theme)}
                            onClick={() => handleClick(c)}
                        >
                            <Box sx={styles.channelAvatar(theme)}>
                                {c.initials ?? c.name.slice(0, 2).toUpperCase()}
                            </Box>
                            <Box sx={styles.channelText}>
                                <Typography sx={styles.channelName}>{c.name}</Typography>
                                <Typography sx={styles.channelMessage}>
                                    {c.lastMessage ?? 'No messages yet'}
                                </Typography>
                            </Box>
                        </Box>
                    ))
                )}
            </Box>
        </Box>
    );
};

export default ChannelsPopover;
